// ── ESP32 / NPG BLE UUIDs ─────────────────────────────────────────────────
const SERVICE_UUID      = "4fafc201-1fb5-459e-8fcc-c5c9c331914b";
const DATA_CHAR_UUID    = "beb5483e-36e1-4688-b7f5-ea07361b26a8";
const CONTROL_CHAR_UUID = "0000ff01-0000-1000-8000-00805f9b34fb";
const BATTERY_CHAR_UUID = "f633d0ec-46b4-43c1-a39f-1ca06d0602e1";

// ── Packet format ─────────────────────────────────────────────────────────
const NUM_CHANNELS      = 3;
const SINGLE_SAMPLE_LEN = 1 + NUM_CHANNELS * 2; // 7 bytes
const BLOCK_COUNT       = 10;
const BATCH_PACKET_LEN  = SINGLE_SAMPLE_LEN * BLOCK_COUNT; // 70 bytes
const DEV_MODE = false;
let mockInterval = null;
let mockTick = 0;
let connectedDevice       = null;
let dataCharacteristic    = null;
let controlCharacteristic = null;
let batteryCharacteristic = null;
let batterySupported      = false;
let prevSampleCounter     = null;
let onDataCallback        = null;

// ── Raw packet logging state ──────────────────────────────────────────────
let packetLogCounter       = 0;
let firstPacketLogged      = false;
let firstParsedLogged      = false;

// ── Battery notification callback ─────────────────────────────────────────
let onBatteryCallback = null;

export function onBatteryChange(cb) {
  onBatteryCallback = cb;
}

// ── Internal: log raw bytes of a DataView ────────────────────────────────
function logRawBytes(label, dataView) {
  const bytes = [];
  for (let i = 0; i < dataView.byteLength; i++) {
    bytes.push(dataView.getUint8(i).toString(16).padStart(2, "0").toUpperCase());
  }
}

// ── Internal: parse one 7-byte sample ────────────────────────────────────
function parseSample(dataView, sampleIndexInBatch = 0) {
  if (dataView.byteLength !== SINGLE_SAMPLE_LEN) {
    console.warn("⚠️ Unexpected sample length:", dataView.byteLength);
    return;
  }

  const counter = dataView.getUint8(0);

  // ── Log the very first parsed sample in full detail ──────────────────
  if (!firstParsedLogged) {
    firstParsedLogged = true;

    // Raw bytes of this sample
    logRawBytes("First Sample", dataView);

    // Parse all interpretations
    const rawUint8  = [];
    const rawInt8   = [];
    const rawUint16BE = [];
    const rawInt16BE  = [];
    const rawUint16LE = [];
    const rawInt16LE  = [];

    for (let i = 0; i < dataView.byteLength; i++) {
      rawUint8.push(dataView.getUint8(i));
      rawInt8.push(dataView.getInt8(i));
    }
    for (let i = 0; i + 1 < dataView.byteLength; i += 2) {
      rawUint16BE.push(dataView.getUint16(i, false));
      rawInt16BE.push(dataView.getInt16(i, false));
      rawUint16LE.push(dataView.getUint16(i, true));
      rawInt16LE.push(dataView.getInt16(i, true));
    }

    // console.group("🔬 [BLE] First Sample — Full byte interpretation");


    // Show what the current parser produces
    const ch1BE = dataView.getInt16(1, false);
    const ch2BE = dataView.getInt16(3, false);
    const ch3BE = dataView.getInt16(5, false);
    const ch1LE = dataView.getInt16(1, true);
    const ch2LE = dataView.getInt16(3, true);
    const ch3LE = dataView.getInt16(5, true);

    console.groupEnd();
  }

  if (prevSampleCounter !== null) {
    const expected = (prevSampleCounter + 1) % 256;
    if (counter !== expected) {
      const gap = (counter - expected + 256) % 256;
      if (gap <= 10) {
        console.warn(`⚠️ Sample loss — expected ${expected}, got ${counter} (gap: ${gap})`);
      }
      prevSampleCounter = counter;
    } else {
      prevSampleCounter = counter;
    }
  } else {
    prevSampleCounter = counter;
  }

  const ch1 = dataView.getInt16(1, false);
  const ch2 = dataView.getInt16(3, false);
  const ch3 = dataView.getInt16(5, false);

  // ── Throttled live logging — every 75 samples (~1 sec @ 75 Hz) ──────
  packetLogCounter++;
  if (packetLogCounter % 75 === 0) {
    const maxVal = Math.max(Math.abs(ch1), Math.abs(ch2), Math.abs(ch3));
    const adcType = maxVal > 1500 ? "12-bit(~2048 center)" : "10-bit(~512 center)";

  }

  if (onDataCallback) {
    onDataCallback({ ch1, ch2, ch3 });
  }
}

// ── Internal: data notification handler ──────────────────────────────────
function handleNotification(event) {
  const value = event.target.value;

  // ── Log the very first raw packet ────────────────────────────────────
  if (!firstPacketLogged) {
    firstPacketLogged = true;

    // console.group("📦 [BLE] First raw notification packet");
    logRawBytes("Full packet", value);

    if (value.byteLength === BATCH_PACKET_LEN) {
      } else if (value.byteLength === SINGLE_SAMPLE_LEN) {
       } else {
      console.warn(`→ UNKNOWN length ${value.byteLength}B — will try to decode as text below`);
      const rawText = new TextDecoder().decode(value.buffer);
    }
    console.groupEnd();
  }

  if (value.byteLength === BATCH_PACKET_LEN) {
    for (let i = 0; i < BATCH_PACKET_LEN; i += SINGLE_SAMPLE_LEN) {
      parseSample(
        new DataView(value.buffer.slice(i, i + SINGLE_SAMPLE_LEN)),
        i / SINGLE_SAMPLE_LEN
      );
    }
  } else if (value.byteLength === SINGLE_SAMPLE_LEN) {
    parseSample(new DataView(value.buffer));
  } else {
    const rawText = new TextDecoder().decode(value.buffer);
    console.warn("⚠️ Unknown packet length:", value.byteLength, "| text:", JSON.stringify(rawText));
  }
}

// ── Internal: battery notification handler ────────────────────────────────
function handleBatteryNotification(event) {
  const value = event.target.value;
  if (value && value.byteLength >= 1) {
    const level = value.getUint8(0);
    if (onBatteryCallback) onBatteryCallback(level);
  }
}

// ── Public: connect ────────────────────────────────────────────────────────
export async function connectEyeDevice() {
    if (DEV_MODE) {
    return { name: "Mock NPG Sensor" };
  }
  const nav = navigator;
  if (!nav.bluetooth) {
    throw new Error("Web Bluetooth API not available — use Chrome on desktop.");
  }


  connectedDevice = await nav.bluetooth.requestDevice({
    filters: [{ namePrefix: "NPG" }],
    optionalServices: [SERVICE_UUID],
  });

  const server = await connectedDevice.gatt.connect();

  let service;
  try {
    service = await server.getPrimaryService(SERVICE_UUID);
  } catch (err) {
    return connectedDevice;
  }

  // Log all characteristics if possible
  try {
    const allChars = await service.getCharacteristics();
    // console.group("📋 All characteristics on this service:");
    allChars.forEach((c, i) => {
    });
    console.groupEnd();
  } catch (err) {
    console.warn("⚠️ Could not enumerate characteristics:", err.message);
  }

  // ── Data characteristic ───────────────────────────────────────────────
  try {
    dataCharacteristic = await service.getCharacteristic(DATA_CHAR_UUID);
  } catch (err) {
    console.warn("⚠️ Data characteristic not found:", err.message);
  }

  // ── Control characteristic ────────────────────────────────────────────
  try {
    controlCharacteristic = await service.getCharacteristic(CONTROL_CHAR_UUID);
  } catch (err) {
    console.warn("⚠️ Control characteristic not found (optional):", err.message);
  }

  // ── Battery characteristic ────────────────────────────────────────────
  batterySupported = false;
  try {
    batteryCharacteristic = await service.getCharacteristic(BATTERY_CHAR_UUID);
    batterySupported = true;
    try {
      const initialVal = await batteryCharacteristic.readValue();
      const initialLevel = initialVal.getUint8(0);
      if (onBatteryCallback) onBatteryCallback(initialLevel);
    } catch (err) {
      console.warn("⚠️ Initial battery read failed:", err.message);
    }

    try {
      await batteryCharacteristic.startNotifications();
      batteryCharacteristic.addEventListener(
        "characteristicvaluechanged",
        handleBatteryNotification
      );
    } catch (err) {
      console.warn("⚠️ Battery notifications unavailable — will rely on poll:", err.message);
    }
  } catch (err) {
    batteryCharacteristic = null;
    batterySupported = false;
  }

  return connectedDevice;
}

// ── Public: read battery on demand (0–100 or null) ────────────────────────
export async function readBattery() {
   if (DEV_MODE) return 85;
  if (!batterySupported || !batteryCharacteristic) {
    return null;
  }
  try {
    const val = await batteryCharacteristic.readValue();
    const level = val.getUint8(0);
    return level;
  } catch (err) {
    console.warn("[readBattery] failed:", err.message);
    return null;
  }
}

// ── Public: start streaming ────────────────────────────────────────────────
export async function startStreaming(onData) {
    if (DEV_MODE) {
    mockTick = 0;
    mockInterval = setInterval(() => {
      mockTick++;
      // first 15s → calm signal, then REM burst
      const inREM = mockTick > 150;
      onData({
        ch1: inREM ? (Math.random() - 0.5) * 600 : (Math.random() - 0.5) * 20,
        ch2: (Math.random() - 0.5) * 20,
        ch3: (Math.random() - 0.5) * 15,  // REF — must stay above 2
      });
    }, 100);
    return;
  }
  if (!dataCharacteristic) {
    console.warn("❌ No data characteristic — startStreaming() aborted.");
    return;
  }

  onDataCallback    = onData;
  prevSampleCounter = null;

  // Reset logging state for fresh connect
  packetLogCounter  = 0;
  firstPacketLogged = false;
  firstParsedLogged = false;

  // console.group("🚀 [BLE] startStreaming() — packet format info");
  console.groupEnd();

  if (controlCharacteristic) {
    try {
      await controlCharacteristic.writeValue(new TextEncoder().encode("START"));
    } catch (err) {
      console.warn("⚠️ Could not send START:", err.message);
    }
  }

  try {
    await dataCharacteristic.startNotifications();
  } catch (err) {
    console.error("❌ startNotifications() failed:", err.message);
    return;
  }

  dataCharacteristic.addEventListener("characteristicvaluechanged", handleNotification);
}

// ── Public: stop streaming ─────────────────────────────────────────────────
export async function stopStreaming() {
  if (!dataCharacteristic) return;

  try {
    dataCharacteristic.removeEventListener("characteristicvaluechanged", handleNotification);
    await dataCharacteristic.stopNotifications();
  } catch (err) {
    console.warn("⚠️ stopNotifications() failed:", err.message);
  }

  if (controlCharacteristic) {
    try {
      await controlCharacteristic.writeValue(new TextEncoder().encode("STOP"));
    } catch (_) {}
  }

  onDataCallback = null;
}

// ── Public: full disconnect ────────────────────────────────────────────────
export async function disconnectEyeDevice() {
   if (DEV_MODE) {
    if (mockInterval) { clearInterval(mockInterval); mockInterval = null; }
    mockTick = 0;
    if (onBatteryCallback) onBatteryCallback = null;
    return;
  }
  await stopStreaming();

  if (batterySupported && batteryCharacteristic) {
    try {
      batteryCharacteristic.removeEventListener(
        "characteristicvaluechanged",
        handleBatteryNotification
      );
      await batteryCharacteristic.stopNotifications();
    } catch (_) {}
  }

  if (connectedDevice && connectedDevice.gatt.connected) {
    connectedDevice.gatt.disconnect();
  }

  connectedDevice       = null;
  dataCharacteristic    = null;
  controlCharacteristic = null;
  batteryCharacteristic = null;
  batterySupported      = false;
  prevSampleCounter     = null;
  onBatteryCallback     = null;

  // Reset log state
  packetLogCounter  = 0;
  firstPacketLogged = false;
  firstParsedLogged = false;
}